import { format } from "date-fns";
import { PlusIcon } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { Card } from "@/components/card";
import { LoadingScreen } from "@/components/loading-screen";
import { Button } from "@/components/ui/button";
import { TabNavigation } from "@/components/ui/tab-navigation";
import { useGetEvents } from "@/features/events/api/get-events";
import { useGetMyEvents } from "@/features/events/api/get-my-events";
import { EventItemSkeleton } from "@/features/events/components/event-item-skeleton";
import { MyEventItem } from "@/features/events/components/my-event-item";
import { MyEventsTable } from "@/features/events/components/my-events-table";
import { NoEvents } from "@/features/events/components/no-events";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { PublicEvent, RoleEnum, RoleGroupEnum } from "@classview/shared-types";
import { createColumnHelper } from "@tanstack/react-table";

import { EventActions } from "../../../features/events/components/event-actions";

export function MyEventsPage() {
  const { hasPermission, organisation } = useOrganisation();

  const {
    data: events,
    isLoading,
    isError,
    isSuccess,
    error,
  } = useGetEvents(organisation.id, {
    isAttending: true,
  });

  const { data: myEvents, isSuccess: isMyEventsSuccess } = useGetMyEvents(
    organisation.id,
  );

  const columnHelper = createColumnHelper<PublicEvent>();
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "eventDetails",
        header: "Event",
        size: 999,
        cell: (info) => {
          return (
            <div className="flex flex-col">
              <span className="text-foreground">
                {info.row.original.session.name}
              </span>
              <span className="capitalize text-muted-foreground">
                {info.row.original.type.toLowerCase()}
              </span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "date",
        header: "Date",
        size: 150,
        cell: (info) => {
          const formattedTime = format(
            info.row.original.session.start,
            "HH:mm",
          );
          const formattedDate = format(
            info.row.original.session.start,
            "eee, MMM d",
          );
          return (
            <div className="flex flex-col">
              <span className="text-foreground">{formattedTime}</span>
              <span className="text-muted-foreground">{formattedDate}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        size: 70,
        cell: (info) => {
          return <EventActions event={info.row.original} />;
        },
      }),
    ],
    [columnHelper],
  );

  if (isError)
    return (
      <section>
        <OnboardingHeader
          title="Events"
          description="View your organisations events"
          className="mb-6"
        />
        <ErrorMessage error={error} />
      </section>
    );

  const eventsByCurrentUser = isMyEventsSuccess ? myEvents : [];

  const displayMyEventsTable =
    eventsByCurrentUser.length > 0 || hasPermission(RoleEnum.EVENTS_CREATE);

  const isSessionGuest = organisation.roleGroup === RoleGroupEnum.SESSION_GUEST;

  return (
    <div>
      <OnboardingHeader
        title="Events"
        className="mb-6"
        actions={
          hasPermission(RoleEnum.EVENTS_CREATE) && (
            <Button asChild>
              <Link to={`/events/create`}>
                <PlusIcon className="mr-1 size-4 text-inherit" /> New Event
              </Link>
            </Button>
          )
        }
      />

      {!isSessionGuest && (
        <TabNavigation
          className="my-6 md:my-10"
          options={[
            { label: "Discover Events", to: "/events/discover" },
            { label: "My Events", to: "/events/my" },
          ]}
        />
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="flex flex-col gap-4">
          {events?.items.map((event) => (
            <MyEventItem key={event.id} event={event} />
          ))}

          {isLoading && (
            <>
              <EventItemSkeleton isHorizontal />
              <EventItemSkeleton isHorizontal />
              <EventItemSkeleton isHorizontal />
            </>
          )}

          {events?.items.length === 0 && <NoEvents />}
        </div>
        {displayMyEventsTable && (
          <div>
            <Card>
              <Card.Header title="My Events" />
              <Card.Content>
                {isLoading && !isSuccess && <LoadingScreen />}
                {eventsByCurrentUser.length > 0 && (
                  <MyEventsTable
                    columns={columns}
                    events={eventsByCurrentUser}
                  />
                )}
                {eventsByCurrentUser.length === 0 &&
                  !isLoading &&
                  isSuccess && (
                    <div className="w-full text-center text-sm text-muted-foreground">
                      You didn't organise any events yet
                    </div>
                  )}
              </Card.Content>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
